import React from 'react';
import { OldTooltip } from '@/components/sushi__old';
import styled from '@emotion/styled';
import { useIntl } from 'react-intl';
import { css, keyframes } from '@emotion/react';

// theme
import { spacing, fontSizes, theme } from '../../utils';

//icons
import { InfoIcon } from '../Icon';
import { Spinner } from '../Spinner';

const getPropsByVariant = ({ variant }) => {
  // default
  const defaultVariantProps = {
    main: {
      position: 'relative',
      width: '100%',
      background: theme.palette.grey[650],
      color: theme.palette.common.white,
      fontWeight: theme.typography.fontWeight.semibold,
      fill: theme.palette.common.white,
      backdropFilter: 'blur(30px)',
      height: '46px',
      padding: spacing.medium,
      borderRadius: theme.shape.borderRadius.md,
      transition: 'all 0.2s ease-in-out',
    },
    hover: {
      background: theme.palette.gold[900],
      borderColor: theme.palette.copper,
      outline: 'none',
    },
    disabled: {
      color: theme.palette.grey[500],
      cursor: 'default',
    },
    error: {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.grey[550],
    },
    placeholder: {
      color: theme.palette.grey[500],
      fontSize: fontSizes['xmedium'],
      transition: 'all 0.2s ease-in-out',
    },
    icon: {
      paddingLeft: '35px',
    },
  };

  // inline crowdfunding
  const inlineCrowdfundingVariantProps = {
    main: {
      position: 'relative',
      width: '100%',
      background: theme.palette.common.white,
      color: theme.palette.grey[800],
      fontWeight: theme.typography.fontWeight.regular,
      fill: theme.palette.common.white,
      backdropFilter: 'blur(30px)',
      height: '46px',
      padding: spacing.medium,
      borderRadius: theme.shape.borderRadius.md,
      border: 'none',
      transition: 'all 0.2s ease-in-out',
    },
    hover: {
      background: theme.palette.common.white,
      outline: 'none',
    },
    disabled: {
      color: theme.palette.grey[500],
      cursor: 'default',
    },
    error: {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.grey[550],
    },
    placeholder: {
      color: theme.palette.grey[800],
      fontSize: fontSizes['xmedium'],
      transition: 'all 0.2s ease-in-out',
    },
    icon: {
      paddingLeft: '35px',
    },
  };

  // inline crowdfunding
  const crowdfundingVariantProps = {
    main: {
      position: 'relative',
      width: '100%',
      background: theme.palette.common.white,
      color: theme.palette.grey[800],
      fontWeight: theme.typography.fontWeight.regular,
      fill: theme.palette.common.white,
      backdropFilter: 'blur(30px)',
      height: '46px',
      padding: spacing.medium,
      borderRadius: '45px',
      transition: 'all 0.2s ease-in-out',
    },
    hover: {
      background: theme.palette.common.white,
      outline: 'none',
    },
    disabled: {
      color: theme.palette.grey[500],
      cursor: 'default',
    },
    error: {
      borderColor: theme.palette.error.main,
      backgroundColor: theme.palette.grey[550],
    },
    placeholder: {
      color: theme.palette.grey[800],
      fontSize: fontSizes['xmedium'],
      transition: 'all 0.2s ease-in-out',
    },
    icon: {
      paddingLeft: '35px',
    },
  };

  const variants = {
    default: defaultVariantProps,
    inlineCrowdfunding: inlineCrowdfundingVariantProps,
    crowdfunding: crowdfundingVariantProps,
  };

  return variants[variant];
};

// extra error style
const LabelWrapper = styled.label`
  display: flex;
  align-items: center;
  flex-direction: column;
  align-items: flex-start;
`;
const LabelLine = styled.span`
  display: flex;
  align-items: center;
  font-size: ${fontSizes['xmedium']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.palette.common.white};
  margin-bottom: 6px;
  cursor: pointer;
  user-select: none;
`;

const Label = styled.span`
  cursor: pointer;
`;

const Description = styled.div`
  font-size: ${fontSizes['medium']};
  color: ${theme.palette.grey[400]};
  margin-bottom: 6px;
`;

const Optional = styled.span`
  color: ${theme.palette.grey[400]};
  font-size: ${fontSizes['small']};
  margin-left: 5px;
  margin-right: 5px;
`;

const ErrorWrapper = styled.div`
  color: ${theme.palette.error.main};
  font-size: ${fontSizes['medium']};
  display: flex;
  align-items: end;
  margin-top: 5px;
`;

const Unit = styled.span`
  color: ${theme.palette.grey[400]};
  font-size: ${fontSizes['medium']};
  position: absolute;
  right: 16px;
  top: 16px;
`;

const animationLoading = keyframes`
  0% {
    box-shadow: inset 50px 20px 130px rgba(241, 192, 134, 1), inset 50px 20px 130px rgba(199, 145, 100, 1);
  }
  50% {
    box-shadow: inset 160px 20px 10px 0 rgba(241,192,134, 0.1);
  }
  100% {
    box-shadow: inset 50px 20px 130px rgba(241, 192, 134, 1), inset 50px 20px 130px rgba(199, 145, 100, 1);
  }
`;

const StyledInputWrapper = styled.span`
  position: relative;
  width: 100%;
  padding: 1px;
  transition: all 0.2s ease-in-out;
`;
const SpinnerWrapper = styled.span`
  width: 10px;
  height: 10px;
  position: absolute;
  right: 40px;
  top: 11px;
`;

const InputWrapper = styled(StyledInputWrapper)<{
  variant: 'inlineCrowdfunding' | 'crowdfunding' | 'default';
}>`
  border-radius: ${(props) => (props.variant === 'crowdfunding' ? '45px' : '5px')};
  background: ${(props) => (props.variant === 'inlineCrowdfunding' ? '#FFFFFF' : '#525252')};
  ${(props) =>
    props.variant !== 'inlineCrowdfunding' &&
    '&:hover { box-shadow: inset 50px 20px 130px rgba(241, 192, 134, 0.64), inset 50px 20px 130px rgba(199, 145, 100, 0.64);}'}
`;
interface TooltipProps {
  text: string;
}

type InputProps = {
  type?: string;
  variant?: 'default' | 'inlineCrowdfunding' | 'crowdfunding';
  label?: string;
  optional?: boolean;
  description?: string;
  error?: string;
  tooltip?: TooltipProps;
  autoFocus?: boolean;
  isLoading?: boolean;
  unit?: string;
  min?: number | string;
  max?: number | string; 
  step?: string;
  withIcon?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  placeholder?: string;
  defaultValue?: string;
  onChange?: any;
  pattern?: string;
  inputRef?: any;
  name?: string;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const i18n = useIntl();
  const variant = props.variant || 'default';
  const propsByVariant = getPropsByVariant({ variant });
  const styleInput = () => {
    const errorVariant = props.error && propsByVariant.error;
    const iconVariant = props.withIcon && propsByVariant.icon;
    return {
      ...propsByVariant.main,
      '&::placeholder': {
        ...(propsByVariant && propsByVariant.placeholder),
      },
      '&:hover, &:focus': !props.disabled && {
        ...(propsByVariant && propsByVariant.hover),
        '&::placeholder': {
          color: theme.palette.grey[400],
        },
      },
      '&:disabled': props.disabled && {
        ...(propsByVariant && propsByVariant.disabled),
      },
      ...iconVariant,
      ...errorVariant,
    };
  };

  return (
    <>
      <LabelWrapper>
        {props.label || props.optional || props.tooltip ? (
          <LabelLine>
            {props.label && <Label>{props.label}</Label>}
            {props.optional && (
              <Optional>{i18n.formatMessage({ id: 'components.input.optional' })}</Optional>
            )}
            {props.tooltip && (
              <OldTooltip
                html={
                  <div className="tooltip text-white text-12 font-semibold rounded-md bg-grey-500 p-2">
                    {props.tooltip.text}
                  </div>
                }>
                <InfoIcon size="small" color={theme.palette.grey[400]} />
              </OldTooltip>
            )}
          </LabelLine>
        ) : null}
        {props.description && <Description>{props.description}</Description>}
        <InputWrapper
          variant={variant}
          className="isLoading"
          css={css`
            animation: ${props.isLoading && animationLoading} 4s ease infinite;
          `}>
          <input
            type={props.type}
            style={{ ...styleInput() }}
            placeholder={props.placeholder}
            onChange={props.onChange}
            autoFocus={props.autoFocus}
            ref={ref}
            pattern={props.pattern}
            disabled={props.disabled}
            defaultValue={props.defaultValue}
            autoComplete={props.autoComplete}
            min={props.min}
            max={props.max}
            step={props.step}
            name={props.name}
          />
          {props.unit && <Unit>{props.unit}</Unit>}
          {props.isLoading ? (
            <SpinnerWrapper>
              <Spinner color="#ffffff" />
            </SpinnerWrapper>
          ) : null}
        </InputWrapper>
      </LabelWrapper>
      {props.error ? (
        <>
          <ErrorWrapper>
            <InfoIcon size="small" color={theme.palette.error.main} />
            {props.error}
          </ErrorWrapper>
        </>
      ) : null}
    </>
  );
});

export default Input;
